// Deterministic question shuffling + centralized grading helpers.
//
// We want question presentation (option order, match column order, etc.)
// to be stable per render so React keys don't thrash, but still varied
// across questions / sessions in a way that doesn't always line up with
// the source-of-truth order returned by the backend.
//
// Grading is centralized so each question type owns the logic for what
// "correct" means — string-compare doesn't work for `matching` because
// the answer is structured.

function hashSeed(input) {
  const str = String(input == null ? '' : input);
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Mulberry32 — small, fast, seeded PRNG.
function createPrng(seed) {
  let state = (seed >>> 0) || 1;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Returns a permutation of [0..length-1], deterministic for a given seed.
export function shuffleIndicesDeterministic(length, seed) {
  const indices = Array.from({length}, (_, i) => i);
  const rand = createPrng(hashSeed(seed));
  for (let i = indices.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices;
}

export function shuffleArrayDeterministic(arr, seed) {
  if (!Array.isArray(arr) || arr.length <= 1) return Array.isArray(arr) ? arr.slice() : [];
  const indices = shuffleIndicesDeterministic(arr.length, seed);
  return indices.map(i => arr[i]);
}

// Shuffle the right-column values of a matching question. We deliberately
// reject the identity permutation so options actually criss-cross instead
// of lining straight across (which would give the answer away). Falls back
// to a single-position rotation when every shuffle attempt collapses to
// identity (only possible with length <= 1, which we already filter).
export function shuffleRightsForMatching(rights, seed) {
  if (!Array.isArray(rights)) return [];
  if (rights.length <= 1) return rights.slice();

  const baseSeed = hashSeed(seed);
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const indices = shuffleIndicesDeterministic(rights.length, baseSeed + attempt);
    const isIdentity = indices.every((idx, i) => idx === i);
    if (!isIdentity) return indices.map(i => rights[i]);
  }

  return rights.map((_, i, arr) => arr[(i + 1) % arr.length]);
}

// Grading: each question type knows how to compare its own answer shape.
// `answer` is whatever the matching renderer passed back (string for the
// scalar types, a {left -> right} map for matching).
export function gradeAnswer(question, answer) {
  if (!question) return false;

  switch (question.type) {
    case 'matching': {
      const pairs = Array.isArray(question.matchPairs) ? question.matchPairs : [];
      if (pairs.length === 0) return false;
      if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return false;
      return pairs.every(pair => answer[pair.left] === pair.right);
    }
    case 'multiple_choice':
    case 'fill_in_the_blank':
    case 'drag_and_drop':
    default:
      return answer === question.correctAnswer;
  }
}
